import React, { ReactNode } from 'react';

import { ArrowDown, ArrowUp } from '@travel/icons/ui';
import { Translate } from '@travel/translation';
import useDeviceType, { MOBILE_SCREEN } from '@travel/traveler-core/hooks/useDeviceType';
import useDialogHandler from '@travel/traveler-core/hooks/useDialogHandler';
import { OtherArea } from '@travel/traveler-core/types/areaDetails';
import { IconTextLink } from '@travel/ui';
import { cx, isEmptyArray } from '@travel/utils';

import { Link } from 'core/universalRouter/Link';
import paths from 'core/universalRouter/paths';

import styles from './otherAreas.module.scss';

export type AREA_CATEGORY =
  | 'SUBDIVISIONS'
  | 'CITIES'
  | 'HOTSPRINGS'
  | 'AIRPORTS'
  | 'TRAINSTATIONS'
  | 'POINTSOFINTEREST';

type Props = {
  wrapperClassName?: string;
  title: ReactNode;
  type: AREA_CATEGORY;
  otherAreas: OtherArea[];
};

function OtherAreas(props: Props) {
  const { otherAreas, wrapperClassName, title, type } = props;
  const deviceType = useDeviceType();
  const isMobile = deviceType === MOBILE_SCREEN;
  const displayCount = isMobile ? 4 : 12;
  const { isOpen, onToggle } = useDialogHandler(false);

  if (isEmptyArray(otherAreas)) {
    return null;
  }

  return (
    <section
      className={cx(styles.sectionWrapper, wrapperClassName)}
      data-testid="otherArea-wrapper"
    >
      <h2 className={styles.title} data-testid="otherArea-title">
        {title}
      </h2>
      <div className={styles.otherAreasContent}>
        <div className={styles.area} data-testid="otherArea-areaContent">
          {otherAreas.slice(0, isOpen ? undefined : displayCount).map(otherArea => (
            <OtherAreaLink
              pathId={otherArea.pathId}
              name={otherArea.name}
              type={type}
              key={otherArea.pathId}
            />
          ))}
        </div>
        {otherAreas.length > displayCount && (
          <div className={styles.toggleButton}>
            <IconTextLink
              text={
                <Translate
                  id={isOpen ? 'AreaTop.Other_Areas.Show_Less' : 'AreaTop.Other_Areas.Show_More'}
                />
              }
              icon={
                isOpen ? (
                  <ArrowUp color="cilantro" size={16} />
                ) : (
                  <ArrowDown color="cilantro" size={16} />
                )
              }
              iconPosition="right"
              onClick={onToggle}
              hoveredColor={isMobile ? 'cilantro' : 'lincolnGreen'}
            />
          </div>
        )}
      </div>
    </section>
  );
}

function OtherAreaLink(props: { pathId: string; name: string; type: AREA_CATEGORY }) {
  const linkPath =
    props.type === 'SUBDIVISIONS'
      ? `${paths.area.pathResolver(props.pathId)}`
      : `${paths.providerList.pathResolver(props.pathId)}`;
  return (
    <Link to={linkPath} className={styles.areaLink} data-testid={`otherArea-link-${props.name}`}>
      <div className={styles.areaName}>{props.name}</div>
    </Link>
  );
}

export default OtherAreas;
